import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { SecretField, TextField } from '@/shared/ui/Field';

import { useSignUp } from '../api/auth';
/* **Read from the model rather than declared here**, which it was until the reset screen needed the
 * same rule. Mirrors `MIN_PASSWORD_LENGTH` in `core.tenancy.domain.local_credential` — a client-side
 * check to catch the common case before a round trip, never the authority. Two copies of one rule is
 * how the sign-up hint and the reset hint come to state different numbers, each passing its own
 * review. */
import { MIN_PASSWORD_LENGTH } from '../model/reset';
import { deliveryOf } from '../model/verification';

/* Standalone, outside `AppShell` (ADR-012): this is the one screen in the platform that runs before
 * a tenant exists. `onSignedUp` mirrors `LoginForm.onLoggedIn` rather than redirecting here directly
 * — the screen decides what "in" means, this form only reports the outcome. */
export function SignupForm({
  onSignedUp,
}: {
  readonly onSignedUp: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const signUp = useSignUp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setAttempted(true);
    /* Refused here rather than sent, because the server refuses it too and the reader would then
       read the refusal without the field beside it. The organisation is required — a signup creates
       a tenancy, and a tenancy nobody can attribute to a customer is a row an operator cannot use. */
    if (
      email.trim() === '' ||
      organisationName.trim() === '' ||
      password.length < MIN_PASSWORD_LENGTH
    )
      return;
    signUp.mutate(
      {
        email: email.trim(),
        password,
        display_name: displayName.trim(),
        organisation_name: organisationName.trim(),
      },
      {
        onSuccess: () => {
          onSignedUp();
        },
        onError: () => {
          setPassword('');
          setResetKey((generation) => generation + 1);
        },
      },
    );
  };

  /* **The form is replaced by a confirmation rather than followed by a redirect.** ADR-014: signup
   * no longer returns a session, so navigating to the dashboard would bounce straight off
   * `RequireAuth` and land the reader on `/login` with no explanation of what just happened.
   *
   * Rendered in place, and it names the address. The person needs to see *which* address the link
   * went to — a typo in the email field is the commonest reason a verification mail never arrives,
   * and it is invisible on a screen that only says "check your inbox". The normalised value from
   * the server is used rather than what was typed, because the fold may differ.
   *
   * **`onSignedUp` is a button rather than an effect.** An automatic redirect would take the
   * address off the screen before it was read, and there is nothing to hurry towards: the account
   * cannot sign in until the link is clicked. */
  if (signUp.isSuccess) {
    const delivery = deliveryOf(signUp.data);
    return (
      <>
        <h1 className="lq-auth__heading">{t('auth.signup.done.title')}</h1>
        <Alert
          tone={delivery === 'undeliverable' ? 'warning' : 'ok'}
          title={t(`auth.signup.done.${delivery}.title`)}
        >
          {t(`auth.signup.done.${delivery}.body`, { email: signUp.data.email })}
        </Alert>
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Button
            tone="primary"
            onClick={() => {
              onSignedUp();
            }}
          >
            {t('auth.signup.done.toSignIn')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      {/* **A heading, not a nested `Card`.** `AuthShell` already renders the card — surface, radius,
          shadow and 28px of padding — so wrapping this form in another one produced two stacked
          surfaces and **two layers of padding**, which is why the submit button came out 292px wide
          inside a 324px box instead of spanning it. Same double-padding class as `.lq-main` over
          `.lq-content`, one component deeper.

          The reference's auth card is a single container with a 16px/600 heading inside it, which is
          what this now is. */}
      <h1 className="lq-auth__heading">{t('auth.signup.title')}</h1>
      <form onSubmit={onSubmit}>
        {/* **First, and required.** A signup creates a tenancy, so the customer it belongs to is the
            subject of this form rather than a detail of it — and the server will refuse without it.
            The hint states the uniqueness rule, because "that name is taken" is a better surprise
            before submitting than after. */}
        <TextField
          label={t('auth.field.organisation')}
          value={organisationName}
          onChange={setOrganisationName}
          hint={t('auth.signup.organisationHint')}
          required
        />
        <TextField
          label={t('auth.field.email')}
          type="text"
          value={email}
          onChange={setEmail}
          required
        />
        <TextField
          label={t('auth.field.displayName')}
          value={displayName}
          onChange={setDisplayName}
        />
        <SecretField
          label={t('auth.field.password')}
          onChange={setPassword}
          hint={t('auth.signup.passwordHint', { minLength: MIN_PASSWORD_LENGTH })}
          required
          resetKey={resetKey}
        />
        {attempted && organisationName.trim() === '' && (
          <Alert tone="warning">{t('auth.signup.organisationRequired')}</Alert>
        )}
        {attempted && passwordTooShort && (
          <Alert tone="warning">
            {t('auth.signup.passwordTooShort', { minLength: MIN_PASSWORD_LENGTH })}
          </Alert>
        )}
        {signUp.isError && (
          <Alert tone="error" title={t('auth.signup.failedTitle')}>
            {signUp.error.message}
          </Alert>
        )}
        <Button type="submit" tone="primary" size="block" busy={signUp.isPending}>
          {t('auth.signup.submit')}
        </Button>
      </form>
      <p className="lq-dim" style={{ marginTop: 'var(--space-4)' }}>
        {t('auth.signup.haveAccount')}{' '}
        <Link to={SCREEN_PATHS.login}>{t('auth.signup.loginLink')}</Link>
      </p>
    </>
  );
}
